import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatCurrency } from "../../../lib/utils";
import { SuperAdminDashboardStats } from "../@types";

interface SubscriptionRevenueChartProps {
  data: SuperAdminDashboardStats;
}

const SubscriptionRevenueChart: React.FC<SubscriptionRevenueChartProps> = ({
  data,
}) => {
  const [showPlanBreakdown, setShowPlanBreakdown] = useState(false);
  const [subscriptionPeriod, setSubscriptionPeriod] = useState<
    "3months" | "6months" | "1year"
  >("3months");

  // Get the appropriate data based on selected period
  const getChartData = () => {
    const yearData = data.subscriptionsByYear;

    switch (subscriptionPeriod) {
      case "3months":
        return yearData.slice(-3);
      case "6months":
        return yearData.slice(-6);
      case "1year":
        return yearData;
      default:
        return yearData;
    }
  };

  const chartData = getChartData();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
          <p className="font-semibold text-gray-800 mb-2">{label}</p>
          {showPlanBreakdown ? (
            <>
              {payload.map((entry: any, index: number) => (
                <p
                  key={index}
                  className="text-sm"
                  style={{ color: entry.color }}
                >
                  {entry.name}: {entry.value.toLocaleString()} subscriptions
                </p>
              ))}
              <p className="text-sm font-semibold text-gray-800 mt-2 pt-2 border-t border-gray-200">
                Total:{" "}
                {payload
                  .reduce((sum: number, entry: any) => sum + entry.value, 0)
                  .toLocaleString()}{" "}
                subscriptions
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-800">
              Total: {payload[0]?.value.toLocaleString()} subscriptions
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="text-gray-500 text-sm font-medium mb-1">
            Subscription revenue
          </h3>
          <p className="text-3xl font-bold text-gray-900">
            {formatCurrency(data.totalSubscriptionRevenue)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 mr-2">
            Show plan breakdown
          </span>
          <button
            onClick={() => setShowPlanBreakdown(!showPlanBreakdown)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              showPlanBreakdown ? "bg-blue-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                showPlanBreakdown ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Period Selector */}
      <div className="flex gap-2 mb-6">
        {(["3months", "6months", "1year"] as const).map((period) => (
          <button
            key={period}
            onClick={() => setSubscriptionPeriod(period)}
            className={`px-3 py-1 text-sm rounded ${
              subscriptionPeriod === period
                ? "bg-blue-100 text-blue-600 border border-blue-600"
                : "bg-white text-gray-600 border border-gray-300"
            }`}
          >
            {period === "3months"
              ? "3 months"
              : period === "6months"
              ? "6 months"
              : "1 year"}
          </button>
        ))}
      </div>

      {/* Recharts Bar Chart */}
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="month"
            tick={{ fill: "#6b7280", fontSize: 12 }}
            axisLine={{ stroke: "#e5e7eb" }}
          />
          <YAxis
            tick={{ fill: "#6b7280", fontSize: 12 }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickFormatter={(value) => value.toLocaleString()}
          />
          <Tooltip content={<CustomTooltip />} />
          {showPlanBreakdown && (
            <Legend wrapperStyle={{ paddingTop: "20px" }} iconType="square" />
          )}

          {showPlanBreakdown ? (
            <>
              <Bar
                dataKey="monthlySubscriptionCount"
                stackId="a"
                fill="#99c9d4"
                name="Monthly plan"
              />
              <Bar
                dataKey="quarterlySubscriptionCount"
                stackId="a"
                fill="#d4d9a8"
                name="Quarterly plan"
              />
              <Bar
                dataKey="yearlySubscriptionCount"
                stackId="a"
                fill="#a8c98f"
                name="Yearly plan"
              />
            </>
          ) : (
            <Bar
              dataKey="subscriptionCount"
              fill="#60a5fa"
              name="Total subscriptions"
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SubscriptionRevenueChart;
